// P6 selling and collecting (SAL-C-04): reads from the sales views, writes through the sale operations. The sale
// sheet asks the server to price its lines (preview_sale), so the screen shows exactly what posting will record.
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { sendOrQueue } from './offline';
import { supabase } from './supabase';

export type SaleStatus = 'paid' | 'unpaid';
export type SaleUnit = 'box' | 'piece';
export interface SaleLineInput {
  product: string;
  unit: SaleUnit;
  quantity: number;
  price: number;
  line_discount: number;
  remove_promo: boolean;
}
export interface PricedLine {
  product: string;
  unit: SaleUnit;
  quantity: number;
  pieces: number;
  catalogue_price: number;
  unit_price: number;
  price_changed: boolean;
  gross: number;
  promotion_id: string | null;
  promotion: string | null;
  promo_pct: number | null;
  promo_applied: boolean;
  promo_amount: number;
  line_discount: number;
  invoice_share: number;
  net: number;
}
export interface SalePreview {
  lines: PricedLine[];
  gross: number;
  promo_total: number;
  discount_total: number;
  invoice_discount: number;
  total: number;
  price_changed: boolean;
  open_boxes: number;
  unpaid_now: number;
  credit_limit: number;
  /** Offline: priced on the phone from catalogue prices, no promotions; the server prices it when it is sent (ADR-128). */
  estimated?: boolean;
}
export interface SaleLineView {
  id: number;
  product_id: string;
  product: string;
  sku: string | null;
  box_capacity: number | null;
  unit: SaleUnit;
  quantity: number;
  catalogue_price: number;
  unit_price: number;
  price_changed: boolean;
  gross: number;
  promotion: string | null;
  promo_pct: number | null;
  promo_applied: boolean;
  promo_amount: number;
  line_discount: number;
  invoice_share: number;
  net: number;
}
export interface Sale {
  id: string;
  number: string;
  round_id: string;
  round_number: string;
  truck_id: string;
  truck_code: string;
  seller_id: string;
  seller_name: string | null;
  collector_id: string;
  collector_name: string | null;
  customer_id: string;
  customer: string;
  status: SaleStatus;
  split_with: string | null;
  split_number: string | null;
  business_date: string;
  at: string;
  gross: string;
  promo_total: string;
  discount_total: string;
  invoice_discount: string;
  total: string;
  price_changed: boolean;
  credit_warning: boolean;
  paid_round_id: string | null;
  paid_at: string | null;
  collection_number: string | null;
  cost: string | null;
  margin: string | null;
  lines: SaleLineView[];
}
export interface Collection {
  id: string;
  number: string;
  round_id: string;
  seller_id: string;
  seller_name: string | null;
  customer_id: string;
  customer: string;
  amount: string;
  business_date: string;
  at: string;
  invoices: string[];
  /** SAL-08: the owner reversed this receipt. */
  reversed?: boolean;
}
export interface RoundSales {
  round_id: string;
  invoices: number;
  paid_invoices: number;
  unpaid_made: string;
  paid_total: string;
}
export interface Promotion {
  id: string;
  name: string;
  condition: 'qty' | 'amount';
  minimum: number;
  pct: number;
  product_id: string | null;
  group_id: string | null;
  priority: number;
  start_date: string;
  end_date: string;
  paused: boolean;
  product: string | null;
  group_name: string | null;
  status: 'active' | 'scheduled' | 'expired' | 'paused';
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}

export const SALES = ['sales'] as const;

export interface SaleFilter {
  round?: string | undefined;
  paidRound?: string | undefined;
  customer?: string | undefined;
  status?: SaleStatus | undefined;
  from?: string | undefined;
  changed?: boolean | undefined;
  seller?: string | undefined;
}
export async function loadSales(f: SaleFilter = {}): Promise<Sale[]> {
  let q = supabase.from('v_sales').select('*').order('at', { ascending: false }).limit(200);
  if (f.round) q = q.eq('round_id', f.round);
  if (f.paidRound) q = q.eq('paid_round_id', f.paidRound);
  if (f.customer) q = q.eq('customer_id', f.customer);
  if (f.status) q = q.eq('status', f.status);
  if (f.from) q = q.gte('business_date', f.from);
  if (f.changed) q = q.eq('price_changed', true);
  if (f.seller) q = q.eq('seller_id', f.seller);
  return rows<Sale>(await q);
}
export async function loadSale(id: string): Promise<Sale | null> {
  return rows<Sale>(await supabase.from('v_sales').select('*').eq('id', id))[0] ?? null;
}
/** Unpaid invoices carried by a Seller, oldest first (SAL-07). */
export async function loadUnpaid(seller: string): Promise<Sale[]> {
  return rows<Sale>(
    await supabase
      .from('v_sales')
      .select('*')
      .eq('collector_id', seller)
      .eq('status', 'unpaid')
      .order('at', { ascending: true }),
  );
}
export async function loadCollections(customer?: string): Promise<Collection[]> {
  let q = supabase.from('v_collections').select('*').order('at', { ascending: false }).limit(100);
  if (customer) q = q.eq('customer_id', customer);
  return rows<Collection>(await q);
}
export async function loadRoundSales(round: string): Promise<RoundSales | null> {
  return rows<RoundSales>(await supabase.from('v_round_sales').select('*').eq('round_id', round))[0] ?? null;
}
export async function loadPromotions(): Promise<Promotion[]> {
  return rows<Promotion>(await supabase.from('v_promotions').select('*').order('priority', { ascending: false }));
}

/** OFF-C-03: the sale priced on the phone — lines at their prices less their discounts, the invoice discount shared by
 * value; no promotions. Only a preview: the server re-prices everything when the sale is sent. */
export function estimateSale(
  lines: SaleLineInput[],
  invoiceDiscount: number,
  capacity: (product: string) => number | null,
  catalogue: (product: string, unit: SaleUnit) => number | null,
  customer: { unpaid: number; limit: number },
): SalePreview {
  const base = lines.map((l) => {
    const gross = Math.round(l.quantity * l.price * 100) / 100;
    return { l, gross, before: Math.max(0, gross - l.line_discount) };
  });
  const sum = base.reduce((a, b) => a + b.before, 0);
  let left = invoiceDiscount;
  const priced: PricedLine[] = base.map((b, i) => {
    const share =
      i === base.length - 1 ? left : sum > 0 ? Math.round((invoiceDiscount * b.before * 100) / sum) / 100 : 0;
    left = Math.round((left - share) * 100) / 100;
    const cat = catalogue(b.l.product, b.l.unit) ?? b.l.price;
    return {
      product: b.l.product,
      unit: b.l.unit,
      quantity: b.l.quantity,
      pieces: b.l.unit === 'box' ? b.l.quantity * (capacity(b.l.product) ?? 1) : b.l.quantity,
      catalogue_price: cat,
      unit_price: b.l.price,
      price_changed: b.l.price !== cat,
      gross: b.gross,
      promotion_id: null,
      promotion: null,
      promo_pct: null,
      promo_applied: false,
      promo_amount: 0,
      line_discount: b.l.line_discount,
      invoice_share: share,
      net: Math.round((b.before - share) * 100) / 100,
    };
  });
  const total = Math.round(priced.reduce((a, p) => a + p.net, 0) * 100) / 100;
  return {
    lines: priced,
    gross: priced.reduce((a, p) => a + p.gross, 0),
    promo_total: 0,
    discount_total: priced.reduce((a, p) => a + p.line_discount, 0) + invoiceDiscount,
    invoice_discount: invoiceDiscount,
    total,
    price_changed: priced.some((p) => p.price_changed),
    open_boxes: 0,
    unpaid_now: customer.unpaid,
    credit_limit: customer.limit,
    estimated: true,
  };
}

export async function previewSale(
  round: string,
  customer: string,
  lines: SaleLineInput[],
  invoiceDiscount: number,
  /** Without signal the sheet still shows a total: an estimate priced on the phone. */
  estimate?: () => SalePreview,
): Promise<SalePreview> {
  if (estimate && typeof navigator !== 'undefined' && navigator.onLine === false) return estimate();
  let result: { data: unknown; error: { message: string; details?: string | null } | null };
  try {
    result = await supabase.rpc('preview_sale', {
      p_round: round,
      p_customer: customer,
      p_lines: lines,
      p_invoice_discount: invoiceDiscount,
    });
  } catch {
    if (estimate) return estimate();
    throw new Refusal('OFFLINE');
  }
  const { data, error } = result;
  if (error && estimate && /fetch|network/i.test(error.message)) return estimate();
  if (error) {
    const code = /^[A-Z_]+$/.test(error.message) ? error.message : 'SERVER_ERROR';
    let detail: Record<string, unknown> = {};
    try {
      detail = error.details ? (JSON.parse(error.details) as Record<string, unknown>) : {};
    } catch {
      detail = {};
    }
    throw new Refusal(code, detail);
  }
  return data as SalePreview;
}
/** What the screens need to show a sale or collection that waits in the offline queue (ADR-128). */
export interface QueueMeta {
  customer: string;
  total: number;
}
export type PostSaleResult = {
  sales: { id: string; number: string; status: SaleStatus; total: number }[];
  opened: number;
  pre_order: string | null;
};
export const isPendingId = (id: string) => id.startsWith('pending-');
export const postSale = (
  round: string,
  customer: string,
  lines: SaleLineInput[],
  invoiceDiscount: number,
  payment: 'full' | 'unpaid' | 'split',
  split: { line: number; quantity: number }[],
  confirmOpen: boolean,
  preOrder: string | null = null,
  meta: QueueMeta = { customer: '', total: 0 },
) =>
  sendOrQueue<PostSaleResult>(
    'post_sale',
    {
      p_round: round,
      p_customer: customer,
      p_lines: lines,
      p_invoice_discount: invoiceDiscount,
      p_payment: payment,
      p_split: split,
      p_confirm_open: confirmOpen,
      p_pre_order: preOrder,
    },
    { kind: 'sale', ...meta },
    (n, id) => ({
      sales: [
        {
          id: `pending-${id}`,
          number: `Pending-${n}`,
          status: payment === 'unpaid' ? 'unpaid' : 'paid',
          total: meta.total,
        },
      ],
      opened: 0,
      pre_order: null,
    }),
  );
export const collectInvoices = (
  round: string,
  customer: string,
  sales: string[],
  meta: QueueMeta = { customer: '', total: 0 },
) =>
  sendOrQueue<{ number: string; amount: number }>(
    'collect_invoices',
    { p_round: round, p_customer: customer, p_sales: sales },
    { kind: 'collection', ...meta },
    (n) => ({ number: `Pending-${n}`, amount: meta.total }),
  );
export const savePromotion = (p: {
  id: string | null;
  name: string;
  condition: 'qty' | 'amount';
  minimum: number;
  pct: number;
  product: string | null;
  group: string | null;
  priority: number;
  start: string;
  end: string;
}) =>
  sendCommand('save_promotion', {
    p_id: p.id,
    p_name: p.name,
    p_condition: p.condition,
    p_minimum: p.minimum,
    p_pct: p.pct,
    p_product: p.product,
    p_group: p.group,
    p_priority: p.priority,
    p_start: p.start,
    p_end: p.end,
  });
export const togglePromotion = (id: string, paused: boolean) =>
  sendCommand('toggle_promotion', { p_id: id, p_paused: paused });
